/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-mixed-spaces-and-tabs */
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import {
  logout,
  setMenu,
  setMenuLateral,
  setRolActivo,
} from "src/redux/features/usuarioSlice";
import {
  obtenerOpcionesSegunRol,
  obtenerPrimerItemConRuta,
  type ItemMenu,
} from "src/utils/menu/menus";
import DialogoConfirmacion from "src/components/dialogo/Confirmacion";
import { Irol, obtenerCodigoRol, Roles } from "src/utils/roles/roles";
import DialogoDatosPersonales from "src/pages/datos-personales/DialogoDatosPersonales";
import { setBreadcrumbs } from "src/redux/features/breadcrumbSlice";
import { RolesId } from "src/utils/constantes";

export interface BarraNavegacionProps {
  menu: ItemMenu[];
}

/**
 * Función que crea la barra de navegación.
 */
const BarraNavegacion: React.FC<BarraNavegacionProps> = ({
  menu,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const estadoUsuario = useAppSelector(
    (estadoApp) => estadoApp.usuario?.usuario ?? null,
  );
  const estadoAplicacion = useAppSelector(
    (estadoApp) => estadoApp.usuario ?? null,
  );
  const rolActivo = useAppSelector(
    (estadoApp) => estadoApp.usuario?.rolActivo ?? null,
  );

  const [abrirMenuOpciones, setAbrirMenuOpciones] = React.useState<
    HTMLElement | undefined
  >(undefined);
  const [abrirMenuUsuario, setAbrirMenuUsuario] = React.useState<
    HTMLElement | undefined
  >(undefined);
  const [mostrarConfirmacionCambioRol, setMostrarConfirmacionCambioRol] =
    React.useState(false);
  const [abrirDatosPersonales, setAbrirDatosPersonales] = React.useState(false);
  const [permitirCerrarDatosPersonales, setPermitirCerrarDatosPersonales] =
    React.useState(false);
  const [rolACambiar, setRolACambiar] = React.useState<Irol>();


  const handleAbrirMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAbrirMenuOpciones(event.currentTarget);
  };

  const handleCerrarMenu = () => {
    setAbrirMenuOpciones(undefined);
  };

  const handleAbrirMenuUsuario = (event: React.MouseEvent<HTMLElement>) => {
    setAbrirMenuUsuario(event.currentTarget);
  };

  const handleCerrarMenuUsuario = () => {
    setAbrirMenuUsuario(undefined);
  };

  const handleDatosPersonales = () => {
    setPermitirCerrarDatosPersonales(true);
    setAbrirDatosPersonales(true);
  };

  const handleCerrarSesion = () => {
    handleCerrarMenuUsuario();
    localStorage.clear();
    dispatch(logout());
    navigate("/", { state: {}, replace: true });
  };

  const handleCambiarContrasena = () => {
    handleCerrarMenuUsuario();
  };

  const obtenerTextoUnidad = () => {
    switch(rolActivo?.id){
      case Roles.ADMIN: 
      return "Admin";
      default: 
      return 'c'
    }
    return "Administrador general";

  }

  const cambiarRol = (rol: Irol) => {
    dispatch(setRolActivo(rol));
    const menuGeneral = obtenerOpcionesSegunRol(rol.id, [
      {
        key: "idUnidadAcademica",
        valor: "",
      },
    ]);
    const menuLateral =
      menuGeneral.length > 0 ? menuGeneral[0].menuLateral : [];
    dispatch(setMenuLateral(menuLateral));
    dispatch(setMenu(menuGeneral));
    localStorage.setItem("rolActivo", JSON.stringify(rol));
    const redireccion = obtenerPrimerItemConRuta(menuLateral);
    setMostrarConfirmacionCambioRol(false);
    const breadcrumbs = [
      { label: redireccion?.label || "", url: redireccion?.path || "/" },
    ];
    dispatch(setBreadcrumbs(breadcrumbs));
    navigate(redireccion?.path || "/");
  };

  const renderBotonMenu = (item: ItemMenu) => (
    <Button
      key={item.route}
      onClick={() => {
        localStorage.setItem('ruta', item.route);
        dispatch(setMenuLateral(item.menuLateral));
        const redireccion = obtenerPrimerItemConRuta(item.menuLateral);
        dispatch(
          setBreadcrumbs([
            { label: item.text, url: redireccion?.path || "/" },
            { label: redireccion?.label || "", url: "/" },
          ]),
        );
        navigate(redireccion?.path || "/");
      }}
      startIcon={<Icon>{item.icon}</Icon>}
      sx={{
        mx: 1,
        color: pathname.includes(item.route) ? "white" : "grey.800",
        display: "flex",
        borderRadius: 2,
        borderBottom: pathname.includes(item.route) ? "2px solid" : "none",
        borderBottomColor: "primary.main",
        bgcolor: pathname.includes(item.route) ? "primary.main" : "none",
        paddingBottom: 0.5,
        paddingX: 2,
        "&:hover": {
          bgcolor: "none",
          backgroundColor: pathname.includes(item.route) ? "primary.main" : "none",
        },
      }}
    >
      {item.text}
    </Button>
  );

  return (
    <AppBar
      position="sticky"
      data-testid="barra-navegacion"
      sx={{ width: "100%", backgroundColor: "white" }}
    >
      <Box>
        <Toolbar
          disableGutters
          sx={{ height: 4, paddingRight: 2 }}
          className="toolbar"
        >
          <Box
            sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}
            data-testid="barra-navegacion-pantalla-grande"
          >
            {menu.map(renderBotonMenu)}
          </Box>
          <Box
            sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}
            data-testid="barra-navegacion-pantalla-pequena"
          >
            <IconButton
              data-testid="boton-menu-opciones"
              size="large"
              aria-label="Cuenta del usuario actual"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleAbrirMenu}
            >
              <MenuIcon sx={{ color: "white" }} />
            </IconButton>
            <Menu
              data-testid="app-bar"
              id="menu-opciones-appbar"
              anchorEl={abrirMenuOpciones}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(abrirMenuOpciones)}
              onClose={handleCerrarMenu}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              {menu.map((item) => (
                <MenuItem
                  data-testid={`boton-menu-${item.text}`}
                  key={item.route}
                  onClick={() => {
                    navigate(item.route);
                    handleCerrarMenu();
                  }}
                >
                  <Icon>{item.icon}</Icon>
                  <Typography textAlign="center">{item.text}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Box sx={{ flexGrow: 0, display: "flex", alignItems: "center" }}>
            <Divider orientation="vertical" flexItem />
            <Chip
              sx={{ mx: 1, color: "primary.main", fontWeight: "bold" }}
              data-testid="chip-nombre-unidad"
              color="primary"
              label={rolActivo?.descripcion}
              variant="outlined"
            />
            <Divider orientation="vertical" flexItem />
            <Avatar
              alt={`${estadoUsuario?.nombres ?? ""} ${
                estadoUsuario?.apellidos ?? ""
              }`}
              sx={{ ml: 1 }}
              src="/static/images/avatar/2.jpg"
            />
            <Tooltip title="Opciones de usuario">
              <IconButton
                sx={{ p: 0 }}
                onClick={handleAbrirMenuUsuario}
                data-testid="boton-menu-perfil-usuario"
              >
                <Icon>keyboard_arrow_down</Icon>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px", maxWidth: "500px" }}
              id="menu-appbar"
              data-testid="menu-perfil-usuario"
              anchorEl={abrirMenuUsuario}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(abrirMenuUsuario)}
              onClose={handleCerrarMenuUsuario}
            >
              <MenuItem
                onClick={handleCerrarMenuUsuario}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "start",
                }}
              >
                <Typography
                  className="w-full"
                  textAlign="left"
                  fontWeight="bold"
                >
                  {`${estadoUsuario?.nombres ?? ""} ${
                    estadoUsuario?.apellidos ?? ""
                  }`}
                </Typography>
                <Typography textAlign="left">
                  {estadoUsuario?.correo ?? ""}
                </Typography>
              </MenuItem>
              <Divider flexItem />
              {/* <br /> */}
              {/* <Divider flexItem />
              
              <MenuItem
                onClick={handleCambiarContrasena}
                data-testid="boton-menu-perfil-usuario-cambiar-contrasena"
                sx={{ display: "flex", gap: 1 }}
              >
                <Icon>key</Icon>
                <Typography textAlign="center">Cambiar contraseña</Typography>
              </MenuItem>
              <Divider /> */}
              <MenuItem
                onClick={handleCerrarSesion}
                data-testid="boton-menu-perfil-usuario-cerrar-sesion"
                sx={{ display: "flex", gap: 1 }}
              >
                <Icon color="error">logout</Icon>
                <Typography textAlign="center" color="error">
                  Cerrar sesión
                </Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Box>
      <DialogoConfirmacion
        mostrarConfirmacion={mostrarConfirmacionCambioRol}
        setMostrarConfirmacion={setMostrarConfirmacionCambioRol}
        titulo="Confirmación"
        mensaje="¿Está seguro que desea cambiar de rol de usuario?"
        onConfirmar={() => {
          cambiarRol(rolACambiar as Irol);
          handleCerrarMenuUsuario();
        }}
      />
      <DialogoDatosPersonales
        mostrarConfirmacion={abrirDatosPersonales}
        setMostrarConfirmacion={setAbrirDatosPersonales}
        permitirCerrar
      />
    </AppBar>
  );
};

export default BarraNavegacion;
